/**
 * In-app actions a start-menu item can trigger instead of navigating.
 * The owning menu component is responsible for handling each action
 * (e.g. opening a modal). Keep this list small and declarative.
 */
export type StartMenuAction = 'create-company';

export type StartMenuLink = {
  label: string;
  /** Navigation target. Omit when `action` is set. */
  url?: string;
  /** Force the link to open in a new tab. Defaults to true for external URLs. */
  external?: boolean;
  /** Small labels rendered as inline badges after the link label. */
  badges?: string[];
  /** When set, activating the item performs an in-app action instead of navigating. */
  action?: StartMenuAction;
};

/**
 * A nested sub-menu under a category. Renders as a second-level SubmenuTrigger
 * inside the category's submenu. Used e.g. to group "NI product modeling"
 * tools under the top-level "Product modeling" category.
 */
export type StartMenuSubcategory = {
  label: string;
  links: StartMenuLink[];
};

export type StartMenuCategory = {
  label: string;
  /** Direct links under this category. */
  links: StartMenuLink[];
  /** Optional nested sub-menus rendered after the direct links. */
  subcategories?: StartMenuSubcategory[];
};

/**
 * The internal-tools menu (the team-only button at the bottom left of every page). This menu is
 * the only index of internal tools: there is no landing page listing them, so a tool missing from
 * here can only be reached by someone who already knows its URL.
 *
 * **Every internal tool with a page of its own gets a row here, in the same PR that adds its
 * route** (`views/root/main/routes/route-declarations/internal-routes.tsx` for `/internal/*`).
 * Pages reached only from another page, such as a detail view behind a list or a route that needs
 * an id, do not. Remove the row in the same PR that removes the route.
 *
 * The unified shell's search panel lists these rows as internal pages for the team
 * (`views/unified-shell/unified-shell-search-results.ts`, `internalPageHits`), so a row's label is
 * also what a team member types to find the page.
 */
export const startMenuCategories: StartMenuCategory[] = [
  {
    label: 'Product modeling',
    links: [
      { label: 'Product hierarchy', url: '/internal/products' },
      { label: 'Prior definitions', url: '/internal/priors' },
      { label: 'Prior assignments', url: '/internal/priors/assign/E1/P/primary' },
      { label: 'Taxonomy visualization', url: '/internal/taxonomy-visualization' },
      { label: 'Product finder', url: '/internal/semantic-search-explorer' },
      { label: 'Generate product description', url: '/internal/generate-product-description' },
      { label: 'Leena-2 runner 🍋‍🟩', url: '/internal/leena2' },
      { label: 'Leena-2 results viewer 🍋‍🟩', url: '/internal/leena2-results' },
      { label: 'Leena-2 economic cost 🍋‍🟩', url: '/internal/leena2-economic-cost' },
      { label: 'Universal impact blacklist', url: '/internal/blacklist/universal-impacts' },
      { label: 'Product-impact reasoning editor', url: '/internal/reasoning-editor/universal-impacts' },
      { label: 'CSRD topics', url: '/internal/csrd-topic-visualization' },
    ],
    subcategories: [
      {
        label: 'NI product modeling',
        links: [
          { label: 'GISE', url: '/internal/gise' },
          { label: 'Constraints', url: '/internal/constraint-sets' },
          { label: 'Impacts', url: '/internal/impacts', badges: ['legacy'] },
        ],
      },
    ],
  },
  {
    label: 'Company modeling',
    links: [
      { label: 'Create a company', action: 'create-company' },
      { label: 'Blanket product sets', url: '/blanket/my-product-sets' },
      { label: 'APG+ batch', url: '/internal/apg-plus-batch' },
      { label: 'Variant scenarios', url: '/internal/variant-scenarios' },
      { label: 'Variant comparison', url: '/internal/variant-scenarios-comparison' },
      { label: 'Variant freezes', url: '/internal/company-variant-freezes' },
    ],
    subcategories: [
      {
        label: 'Imports & identifiers',
        links: [
          { label: 'Company identifiers', url: '/internal/company-identifiers' },
          { label: 'Companies from Demandbase', url: '/internal/insideview-import' },
          {
            label: 'Import microwork product sets',
            url: '/internal/import-microwork-product-sets',
            badges: ['legacy'],
          },
        ],
      },
    ],
  },
  {
    label: 'Portfolio modeling',
    links: [
      { label: 'Portfolio data import', url: '/internal/import-data' },
      { label: 'Import unweighted portfolios', url: '/internal/import-portfolios' },
      { label: 'Import sustainable bonds', url: '/internal/import-sustainable-bonds' },
      { label: 'Import from SEC EDGAR', url: '/internal/sec-edgar-import' },
      { label: 'Add/delete weight sets via external API', url: '/internal/external-api/weight-set-operations' },
      { label: 'Estimate index', url: '/internal/estimate-index' },
    ],
  },
  {
    label: 'Agents and AI',
    links: [
      { label: 'Upright Agent workbench', url: '/internal/agent-workbench' },
      { label: 'APG workbench', url: '/internal/apg-workbench' },
      { label: 'Usage', url: '/internal/usage' },
      { label: 'LLM cost', url: '/internal/llm-cost' },
      { label: 'Model endpoints', url: '/internal/model-endpoints' },
      { label: 'Leena-2 topic checks', url: '/internal/leena2-topic-checks' },
      { label: 'Upbot sessions', url: '/internal/upbot-sessions' },
    ],
  },
  {
    label: 'Eevo',
    // The same groups, rows and labels as the Eevo internal pages' own rail
    // (`eevo-internal-rail.tsx`), so the two read as one menu: change them together.
    links: [
      { label: 'Business', url: '/internal/eevo-business' },
      { label: 'Accounts', url: '/internal/eevo-accounts' },
      // How signed-up accounts use Eevo over time: decisions, active accounts, returning share.
      { label: 'Usage', url: '/internal/eevo-usage' },
    ],
    subcategories: [
      {
        label: 'Marketing',
        links: [
          // Landing page to sign-up, first decision, MCP and purchase, by source and A/B variant.
          { label: 'Funnel', url: '/internal/eevo-funnel' },
          // The @eevo reply bot on X: its mode, approval queue, runs, opt-outs and spend.
          { label: 'X bot', url: '/internal/eevo-x-bot' },
          // Product-update emails to Eevo's users, by paid/unpaid group and by account.
          { label: 'Product updates', url: '/internal/eevo-product-updates' },
          // Change notices of Eevo's data processing agreement, sent by hand to its signatories (PLT-7786).
          { label: 'DPA change notices', url: '/internal/eevo-dpa-notices' },
          // Invite programme: starting invites, invite grants and sign-ups through invite links (PLT-7779).
          { label: 'Invites', url: '/internal/eevo-invites' },
          // Promo codes account admins redeem on the billing page for free credit (PLT-7765).
          { label: 'Promo codes', url: '/internal/eevo-promo-codes' },
        ],
      },
      {
        label: 'Operations',
        links: [
          // Eevo capacity's subpages. Leena-2's endpoints stay on Model endpoints.
          { label: 'Performance', url: '/internal/eevo-capacity/performance' },
          { label: 'Infrastructure', url: '/internal/eevo-capacity/infrastructure' },
          { label: 'Bottlenecks', url: '/internal/eevo-capacity/bottlenecks' },
          { label: 'GPUs', url: '/internal/eevo-capacity/gpus' },
          // The Eevo-1 versions served, and promoting a new one without a code change (PLT-8131).
          { label: 'Versions', url: '/internal/eevo-versions' },
          // Refused and failed requests, sign-ins and sign-ups, and who they affected.
          { label: 'Errors', url: '/internal/eevo-errors' },
          // The eevo@eevo.io bot: its mode, budget, surge mode and every email it received.
          { label: 'Email bot', url: '/internal/eevo-mail-bot' },
          // The Slack app's bot: its mode, the workspaces that installed it, its turns and spend.
          { label: 'Slack bot', url: '/internal/eevo-slack-bot' },
          // The chat on the Eevo console's start page: who sees it, its model and its limits.
          { label: 'Chat', url: '/internal/eevo-web-chat' },
          // status.eevo.io: component statuses, auto mode, and posting incidents and maintenance.
          { label: 'Status page', url: '/internal/eevo-status' },
        ],
      },
      {
        label: 'Related',
        links: [{ label: 'Eevo console', url: '/arvo' }],
      },
    ],
  },
  {
    label: 'Design',
    links: [
      // PLT-5926: fixture-driven prototypes of product surfaces. Listed because the point
      // of the route (as opposed to the Storybook story of the same section) is that a
      // reviewer who does not build the frontend can reach it. One link rather than one
      // per prototype: the index page lists them, and the harness switches between them.
      { label: 'Prototypes', url: '/internal/proto' },
      // Served by upserve as a separate static bundle, not by this app's router, so
      // `navigate()` would land on the app's not-found page. `external` opens it in a new tab,
      // where the browser requests it from the server.
      { label: 'Storybook', url: '/internal/storybook/', external: true },
    ],
  },
  {
    label: 'User management',
    links: [
      { label: 'Organizations', url: '/internal/organizations' },
      { label: 'Access', url: '/internal/access' },
      { label: 'Access requests', url: '/internal/access/requests' },
      { label: 'User groups', url: '/internal/user-groups' },
      { label: 'User creation', url: '/internal/create-users' },
      { label: 'Sign-in links', url: '/internal/sign-in-links' },
      { label: 'Team default organization', url: '/internal/team-default-organization' },
    ],
  },
  {
    label: 'Operations',
    links: [
      { label: 'Settings', url: '/internal/settings' },
      { label: 'Data releases', url: '/internal/versioning' },
      { label: 'Job queue', url: '/internal/jobs' },
      { label: 'Dirty weight sets', url: '/internal/dirty-weight-sets' },
      { label: 'Event log', url: '/internal/log' },
      { label: 'Model update', url: '/internal/compute' },
      { label: 'Deliveries', url: '/internal/deliveries' },
      { label: 'Memcached', url: '/internal/memcached' },
      { label: 'Advanced technical actions', url: '/internal/advanced-technical-actions' },
      { label: 'Usage limits', url: '/internal/usage-limits' },
      { label: 'Probe log', url: '/internal/probes' },
      {
        label: 'Endpoint metrics in Grafana',
        url: 'https://grafana.production.uprightplatform.io/d/e0823d2a-3219-4727-8666-b3398ba464a5/physical-telemetry-dashboard?orgId=1&refresh=30s',
        badges: ['VPN'],
      },
      { label: 'Jaeger', url: 'https://jaeger.uprightplatform.io', badges: ['VPN'] },
      {
        label: 'RDS monitoring',
        url: 'https://eu-west-1.console.aws.amazon.com/rds/home?region=eu-west-1#database:id=production-rds;is-cluster=false;tab=monitoring',
        badges: ['AWS console'],
      },
      {
        label: 'Linear active projects',
        url: 'https://linear.app/upright/view/active-projects-781eac4272fd',
        badges: ['Linear login'],
      },
    ],
  },
  {
    label: 'Miscellaneous',
    links: [
      // Rendered from the markdown in docs/platform/.
      { label: 'Documentation', url: '/internal/docs' },
      { label: 'Data sources', url: '/internal/data-sources' },
      { label: 'Monthly Quandl update', url: '/internal/quandl-update' },
    ],
  },
];
